// Auth for the aura-courses Firebase project. Normally the session is opened
// by Studio's own sign-in (services/auth.ts replays the Google credential).
// This module exposes that session's state and `signInToCourses()`, the
// "Sign in to edit courses" button: it re-runs Studio's own Google popup
// (same origin) and replays the credential, for admins whose Studio session
// predates this feature or whose replay failed at sign-in. With a Studio
// user signed in that popup is a re-authentication of that user, so picking
// another account fails (auth/user-mismatch) instead of replacing the Studio
// session. It never opens a popup on the courses app, whose authDomain is
// cross-origin from the Studio site (a cross-origin popup relay fails
// silently, see .env.prod).
//
// Local dev only: Studio normally runs with no Firebase config of its own, so
// there is no Studio popup to reuse; then, and only then, the popup opens on
// the courses app directly (localhost is fine for that).

import {
  GoogleAuthProvider,
  onAuthStateChanged,
  reauthenticateWithPopup,
  signInWithPopup,
  signOut as fbSignOut,
  type User,
} from "firebase/auth";
import { FIREBASE_ENABLED } from "../../config/env";
import { getCoursesAuth, getFirebaseAuth } from "../../config/firebase";
import { ACCESS_DENIED_MESSAGE, isAllowedDomain, replayCredentialIntoCourses, signInWithGooglePopup } from "../auth";

export function getCoursesUser(): User | null {
  return getCoursesAuth()?.currentUser ?? null;
}

export function onCoursesAuthChange(cb: (user: User | null) => void): () => void {
  const auth = getCoursesAuth();
  if (!auth) {
    cb(null);
    return () => {};
  }
  return onAuthStateChanged(auth, cb);
}

export async function signInToCourses(): Promise<User> {
  const auth = getCoursesAuth();
  if (!auth) throw new Error("Courses are not configured for this deployment.");

  if (FIREBASE_ENABLED) {
    const studioUser = getFirebaseAuth()?.currentUser;
    const result = studioUser
      ? await reauthenticateWithPopup(studioUser, new GoogleAuthProvider())
      : await signInWithGooglePopup();
    if (!isAllowedDomain(result.user.email)) throw new Error(ACCESS_DENIED_MESSAGE);
    await replayCredentialIntoCourses(result);
    const user = auth.currentUser;
    if (!user) throw new Error("The courses project did not accept the Studio sign-in.");
    return user;
  }

  // Local dev without a Studio Firebase config.
  const result = await signInWithPopup(auth, new GoogleAuthProvider());
  if (!isAllowedDomain(result.user.email)) {
    await fbSignOut(auth);
    throw new Error(ACCESS_DENIED_MESSAGE);
  }
  return result.user;
}

export async function signOutOfCourses(): Promise<void> {
  const auth = getCoursesAuth();
  if (auth) await fbSignOut(auth);
}

export function errorText(e: unknown): string {
  if (e instanceof Error) {
    // Firestore's permission error reads "Missing or insufficient permissions."
    if (/insufficient permissions/i.test(e.message)) {
      return "Not allowed. Sign in to the courses project with an Aura account.";
    }
    if (/user-mismatch/i.test(e.message)) {
      return "That is a different Google account. Pick the one you are signed in to Studio with.";
    }
    // Replay refused: the Studio client id is not whitelisted in aura-courses yet.
    if (/invalid-credential|operation-not-allowed|unauthorized-domain/i.test(e.message)) {
      return `${e.message} — is Studio's web client id whitelisted in the aura-courses Google sign-in settings? (README, "Courses")`;
    }
    return e.message;
  }
  return String(e);
}
